/**
 * Response-recorder orchestrator (E6-S5).
 *
 * Wraps E4's `Recorder` for the recipient → vault-owner reply flow. The
 * cryptographic mirror of the seal flow: a fresh CEK is generated on the
 * recipient's device, the content is AES-GCM-encrypted, and the CEK is
 * HPKE-wrapped to the VAULT OWNER's pubkey instead of the recipient's.
 *
 * The orchestrator does NOT depend on a particular UI framework — it
 * exposes a small lifecycle (start, stop, send) the caller wires to
 * buttons. The 28-record-response.html screen is wired to this orchestrator.
 */
// Import the browser-safe crypto modules DIRECTLY, not the barrel
// (`crypto/index.js`) — the barrel re-exports server-only modules (pii /
// local-kms / triggers dobHash → node:crypto) that break the browser bundle
// once this file is vendored into the recipient app. Same fix as seal.ts (S5).
import { generateCek, wrapCekForRecipient } from '../../crypto/cek.js';
import { importPublicKeyFromServer } from '../../crypto/keys.js';
export async function sealResponse(args) {
    const fetchImpl = args.fetchImpl ?? globalThis.fetch.bind(globalThis);
    const ctx = await args.api.fetchResponseContext(args.sourceMessageId);
    let cek = generateCek();
    try {
        // 1. AES-GCM encrypt content with this CEK.
        const iv = randomBytes(12);
        const ciphertext = await aesGcmEncrypt({
            cek,
            iv,
            plaintext: args.contentBytes,
            // Use the source message id as AAD so a server adversary can't
            // transplant a response onto a different source message.
            aad: new TextEncoder().encode(args.sourceMessageId),
        });
        // 2. Wrap the CEK to the vault owner's pubkey.
        const ownerKey = await importPublicKeyFromServer(ctx.vault_owner_public_key);
        const cekCiphertext = await wrapCekForRecipient(cek, ownerKey.publicKey, args.sourceMessageId);
        // 3. Upload the encrypted blob to R2 (video/voice) or inline (letter).
        let r2Key = null;
        let letterCiphertextB64 = null;
        if (args.format === 'letter') {
            letterCiphertextB64 = bytesToBase64(ciphertext);
        }
        else {
            const presigned = await args.api.presignResponseUpload({
                sourceMessageId: args.sourceMessageId,
                contentLengthBytes: ciphertext.byteLength,
            });
            const putResp = await fetchImpl(presigned.url, {
                method: 'PUT',
                headers: presigned.requiredHeaders,
                body: toAb(ciphertext),
            });
            if (!putResp.ok) {
                throw new Error(`Encrypted upload failed (HTTP ${putResp.status}).`);
            }
            r2Key = presigned.key;
        }
        // 4. POST the seal record.
        const result = await args.api.postResponse({
            sourceMessageId: args.sourceMessageId,
            format: args.format,
            cekCiphertext,
            ownerPublicKey: ctx.vault_owner_public_key,
            cekAlgorithmVersion: ctx.cek_algorithm_version,
            ivB64: bytesToBase64(iv),
            r2Key,
            letterCiphertextB64,
        });
        return result;
    }
    finally {
        if (cek !== null) {
            cek.fill(0);
            cek = null;
        }
    }
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function aesGcmEncrypt(args) {
    const subtle = getSubtle();
    const key = await subtle.importKey('raw', toAb(args.cek), { name: 'AES-GCM' }, false, ['encrypt']);
    const ct = await subtle.encrypt({
        name: 'AES-GCM',
        iv: toAb(args.iv),
        additionalData: toAb(args.aad),
        tagLength: 128,
    }, key, toAb(args.plaintext));
    return new Uint8Array(ct);
}
function getSubtle() {
    const c = globalThis.crypto;
    if (c === undefined || c.subtle === undefined) {
        throw new Error('globalThis.crypto.subtle is unavailable.');
    }
    return c.subtle;
}
function randomBytes(n) {
    const out = new Uint8Array(n);
    const c = globalThis.crypto;
    if (c === undefined || typeof c.getRandomValues !== 'function') {
        throw new Error('globalThis.crypto.getRandomValues is unavailable.');
    }
    c.getRandomValues(out);
    return out;
}
function toAb(view) {
    const copy = new Uint8Array(view.byteLength);
    copy.set(view);
    return copy.buffer;
}
function bytesToBase64(b) {
    if (typeof Buffer !== 'undefined')
        return Buffer.from(b).toString('base64');
    let bin = '';
    for (const ch of b)
        bin += String.fromCharCode(ch);
    return globalThis.btoa(bin);
}
